// src/components/Cards/FormCard.jsx

import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Tooltip,
  Box,
  Menu,
  MenuItem,
  IconButton
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { unpublishTemplate } from '../../utils/unpublishTemplate';
import { useAuth } from '../../contexts/AuthContext';
import { useStorageUrl } from '../../hooks/useStorageUrl';

/**
 * Screenshot Storage URL policy
 * -----------------------------------------------------------------------------
 * ENTERPRISE CONTEXT
 * - FormCard is used across template gallery surfaces:
 *   OrganizationTemplateTab, AppTemplateTab, RecentTab, and OpenFormPickerDialog.
 * - The screenshot paths passed into FormCard are form screenshots generated by
 *   the screenshot service, not arbitrary private DAM assets.
 * - useStorageUrl is intentionally conservative and does not infer public access
 *   from Storage path shape alone, because private DAM derivatives can share
 *   similar path patterns.
 *
 * RESULT
 * - Screenshot callers explicitly declare `assetType: 'screenshot'`.
 * - Public-form-image screenshot assets can resolve directly to environment-aware
 *   GCS URLs.
 * - Private DAM assets still require signed/backend URLs elsewhere.
 * -----------------------------------------------------------------------------
 */
const SCREENSHOT_STORAGE_URL_POLICY = Object.freeze({
  assetType: 'screenshot',
  preferPublicUrl: true,
  allowPublicFallback: true,
});

/* identical to AssetCard: transparent 1×1 fallback pixel */
const FALLBACK_PIXEL =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"/>';

const FORM_CARD_FALLBACK_COLORS = ['#4285F4', '#DB4437', '#F4B400', '#0F9D58'];

const isBrowserUrl = (value) => (
  typeof value === 'string' &&
  /^https?:\/\//i.test(value)
);

const isStorageAssetPath = (value) => (
  typeof value === 'string' &&
  value.startsWith('assets/')
);

const deriveStoragePathFromBrowserUrl = (url) => {
  if (!isBrowserUrl(url)) return null;

  try {
    const match = url.match(/\/(assets\/[^?]+)/);
    if (!match) return null;

    return decodeURIComponent(match[1]);
  } catch (_) {
    return null;
  }
};

const getStableFallbackColor = (form, title) => {
  const stableKey = String(form?.id || form?.formId || title || 'fallback');
  let hash = 0;

  for (let i = 0; i < stableKey.length; i += 1) {
    hash = ((hash << 5) - hash) + stableKey.charCodeAt(i);
    hash |= 0; // Convert to 32-bit integer
  }

  return FORM_CARD_FALLBACK_COLORS[
    Math.abs(hash) % FORM_CARD_FALLBACK_COLORS.length
  ];
};

/**
 * FormCard:
 *  - Always has a 1px border around the top image => no "jump" on hover
 *  - If selected, border color is #1976d2
 *  - On hover (when not selected), the border color changes to #1976d2
 *  - Title below with minimal padding, small top margin, truncated if too long
 *  - Uses form.screenshotUrl if available, else random color fallback
 *  - Shows a "Draft" overlay if form.status !== 'published'
 *  - Anchors the screenshot at the top so the top isn't cut off
 */
export default function FormCard({
  form,
  onClick,
  isSelected = false,
  showOverflowMenu = false,
  isTemplateInGallery = false,        
  onConfigureTemplate = null,
  onDuplicateTemplate = null,      
  templateScope = 'organization',
  onUnpublishSuccess = null

}) {
  // Fallback if no form.title
  const title = form?.title || 'Untitled Form';

  // 1) New: handle Storage *path* → browser-usable URL
/*───────────────────────────────────────────────
  Decide what we have and resolve if needed
───────────────────────────────────────────────*/
const legacyLooksLikeUrl =
  typeof form?.screenshotUrl === 'string' &&
  /^https?:\/\//i.test(form.screenshotUrl);

const legacyLooksLikePath =
  isStorageAssetPath(form?.screenshotUrl);

console.log('[FormCard] screenshot fields:', {
  screenshotUrlField : form?.screenshotUrl,
  legacyLooksLikeUrl,
  legacyLooksLikePath,
  screenshotPathField: form?.screenshotPath,
  screenshotThumb    : form?.screenshotThumbnail,
  screenshotAssetType: form?.screenshotAssetType || null,
});

/* 1️⃣ primary candidates straight from document */
const thumbPath =
  form?.screenshotThumbnail ||                    // 400-px thumb
  (
    legacyLooksLikePath &&
    /\/v\d+_(thumbnail|grid_(400|800)|micro_(32|64))\./i.test(form.screenshotUrl)
      ? form.screenshotUrl
      : null
  );

let screenshotPath =
  form?.screenshotPath      ||                    // new canonical field
  form?.screenshotPreviewPath ||                  // enriched in-memory helper
  (
    legacyLooksLikePath &&
    !/\/v\d+_(thumbnail|grid_(400|800)|micro_(32|64))\./i.test(form.screenshotUrl)
      ? form.screenshotUrl
      : null
  ) ||
  null;

/* 2️⃣ if we still have no path but *do* have a full URL,
      try to derive the Storage path from it                        */
if (!screenshotPath && legacyLooksLikeUrl) {
  const derived = deriveStoragePathFromBrowserUrl(form.screenshotUrl);

  if (derived) {
    screenshotPath = derived;
    console.log('[FormCard] derived path from legacy URL →', screenshotPath);
  }
}

console.log('[FormCard] resolved screenshotPath ⇒', screenshotPath);

/* 3️⃣ ask Storage URL resolver for browser-usable URLs.
 *
 * ENTERPRISE:
 * These paths are form screenshots. Pass explicit screenshot policy so
 * useStorageUrl can return environment-aware public GCS URLs without
 * attempting Firebase Storage getDownloadURL() first.
 */
const {
  url: thumbUrl,
  error: thumbErr,
  source: thumbSource,
} = useStorageUrl(thumbPath, {
  ...SCREENSHOT_STORAGE_URL_POLICY,
  debugLabel: 'FormCard.thumbnail',
});

const {
  url: resolvedUrl,
  error: fullErr,
  source: fullSource,
} = useStorageUrl(screenshotPath, {
  ...SCREENSHOT_STORAGE_URL_POLICY,
  debugLabel: 'FormCard.full',
});

const legacyPathForResolution =
  legacyLooksLikePath &&
  form.screenshotUrl !== thumbPath &&
  form.screenshotUrl !== screenshotPath
    ? form.screenshotUrl
    : null;

const {
  url: legacyUrlFromPath,
  error: legacyPathErr,
  source: legacyPathSource,
} = useStorageUrl(legacyPathForResolution, {
  ...SCREENSHOT_STORAGE_URL_POLICY,
  debugLabel: 'FormCard.legacyPath',
});

console.log('[FormCard] thumbUrl:', thumbUrl, 'thumbErr:', thumbErr,
            'thumbSource:', thumbSource,
            'fullUrl:', resolvedUrl, 'fullErr:', fullErr,
            'fullSource:', fullSource,
            'legacyUrlFromPath:', legacyUrlFromPath,
            'legacyPathErr:', legacyPathErr,
            'legacyPathSource:', legacyPathSource);

/* 4️⃣ final URL we try to show.
 *
 * Prefer thumbnail first for fast grid rendering, then full-size original,
 * then legacy path/url compatibility.
 */
const screenshotUrlFinal =
  thumbUrl
  || resolvedUrl
  || legacyUrlFromPath
  || (legacyLooksLikeUrl ? form.screenshotUrl : null);  // very old docs


console.log('[FormCard] final screenshotUrl ⇒', screenshotUrlFinal);

/*–––– local loading / error state ––––*/
const [imgSrc   , setImgSrc   ] = useState(screenshotUrlFinal || FALLBACK_PIXEL);
const [imgLoaded, setImgLoaded] = useState(false);

/* when THUMB appears, show it instantly;
   when FULL arrives, swap it in (no overlay reset)             */
useEffect(() => {
  const nextSrc = screenshotUrlFinal || FALLBACK_PIXEL;

  if (nextSrc !== imgSrc) {
    setImgSrc(nextSrc);

    /* Only re-enable skeleton if the *very first* bitmap hasn’t loaded */
    if (!imgLoaded) setImgLoaded(false);
  }
}, [screenshotUrlFinal, imgSrc, imgLoaded]);


  // 2) If no screenshot, pick a deterministic fallback colour
  const randomColor = useMemo(
    () => getStableFallbackColor(form, title),
    [form?.id, form?.formId, title],
  );

  // 3) Determine if form is draft (status !== 'published')
  const isDraft = form?.status !== 'published';

  // Local state for MoreVert menu
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const { currentUser, hasPermission } = useAuth();

  // Determine if the signed‑in user can unpublish this template
  const canUnpublish = hasPermission?.(
    templateScope === 'global'
      ? 'publishGlobalTemplates'
      : 'publishOrganizationTemplates'
  );

  // Handler: open menu
  const handleMenuOpen = (event) => {
    event.stopPropagation(); // prevent parent onClick if needed
    setAnchorEl(event.currentTarget);
  };

  // Handler: close menu
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Stub for “Configure” option
  const handleConfigure = (event) => {
    event.stopPropagation();
    handleMenuClose();

    // 1) If we’re in the gallery AND we have onConfigureTemplate => call duplication
    if (isTemplateInGallery && typeof onConfigureTemplate === 'function') {
      onConfigureTemplate(form);
      return;
    }

    // 2) Otherwise, do the normal or fallback flow
//    console.log('[FormCard] Configure clicked for form (non-template fallback):', form.id);
  };


  // Full “Remove from gallery” (un‑publish) handler
  const handleRemoveFromGallery = async (event) => {
    event.stopPropagation();
    handleMenuClose();

    if (!canUnpublish) {
      console.warn('[FormCard] User lacks permission to unpublish template');
      return;
    }

//    console.log('[FormCard] Unpublishing template…', {
//      formId: form.id,
//      scope : templateScope
//    });

    try {
      const result = await unpublishTemplate({
        formId       : form.id,
        templateScope,
        currentUser
      });

      console.log('[FormCard] Unpublish result:', result);

      if (result.status === 'success') {
        // Inform parent so it can refresh lists / toasts
        onUnpublishSuccess?.(form.id);
      } else {
        console.error('[FormCard] Unpublish failed:', result.message);
        // optional: surface a toast/snackbar here
      }
    } catch (err) {
      console.error('[FormCard] Unexpected error while unpublishing:', err);
    }
  };


  return (
    <Card
      onClick={onClick}
      elevation={0}
      sx={{
        boxShadow: 'none',
        backgroundColor: 'transparent',
        width: 200,
        display: 'flex',
        flexDirection: 'column',
        cursor: onClick ? 'pointer' : 'default'
      }}
    >
      {/*
        Image container with 1px border + hover effect
        Keep height: 150 EXACTLY as in your code
      */}
      <Box
        sx={{
          width: '100%',
          height: 150,
          border: '1px solid',
          borderColor: isSelected ? '#1976d2' : '#ccc',
          overflow: 'hidden',
          transition: 'border-color 0.2s',
          position: 'relative',
          '&:hover': {
            borderColor: '#1976d2'
          }
        }}
      >
{/* ░░ Skeleton-style overlay while the real bitmap loads ░░ */}
{!imgLoaded && imgSrc === FALLBACK_PIXEL && (
  <Box
    sx={{
      position: 'absolute',
      inset   : 0,
      bgcolor : '#e0e0e0',        // neutral grey (like MUI Skeleton)
    }}
  />
)}

{/* Actual screenshot (or 1 × 1 fallback pixel) */}
<CardMedia
  component="img"
  src={imgSrc}
  alt="Form Screenshot"
  onLoad={() => {
    console.log('[FormCard] image loaded OK:', imgSrc);
    setImgLoaded(true);
  }}
  onError={() => {
    console.warn('[FormCard] image 404 → fallback pixel:', imgSrc);
    if (imgSrc !== FALLBACK_PIXEL) setImgSrc(FALLBACK_PIXEL);
  }}
  sx={{
    display       : imgLoaded ? 'block' : 'none',
    width         : '100%',
    height        : '100%',
    objectFit     : 'cover',
    objectPosition: 'top',
  }}
/>

{/* Final coloured block if we ended up with the pixel fallback */}
{imgSrc === FALLBACK_PIXEL && imgLoaded && (
  <Box
    sx={{
      position: 'absolute',
      inset   : 0,
      bgcolor : randomColor,
    }}
  />
)}


        {/* Draft overlay if status !== 'published' */}
        {isDraft && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(80, 80, 80, 0.35)',
              zIndex: 1,
              pointerEvents: 'none'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.65)',
                color: '#fff',
                fontSize: '0.65rem',
                fontWeight: 600,
                px: 1,
                py: 0.25,
                borderRadius: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                pointerEvents: 'none'
              }}
            >
              Draft
            </Box>
          </Box>
        )}
      </Box>

      {/* Title row, with optional MoreVert icon on the right */}
      <CardContent
        sx={{
          p: 0,
          display: 'flex',
          alignItems: 'center' // vertically center text + icon
        }}
      >
        <Tooltip title={title}>
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              mb: 0,
              ml: 0.5,
              lineHeight: 1,
              textAlign: 'left',
              fontSize: '0.80rem',
              fontWeight: 600,
              color: '#424242',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              flexGrow: 1 // ensures text shrinks so icon won't overlap
            }}
          >
            {title}
          </Typography>
        </Tooltip>

        {showOverflowMenu && (
          <>
            {/* MoreVert icon with reduced padding/size */}
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{
                // CHANGED: reduce padding for alignment
                p: 0.5,
                mr: 0,
                mt: 1
              }}
              aria-label="Options"
            >
              {/* CHANGED: smaller fontSize for the icon */}
              <MoreVertIcon sx={{ fontSize: '1rem' }} />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              PaperProps={{
                // CHANGED: widen slightly, reduce text size in items
                style: { width: '16ch' }
              }}
            >
              {/* CHANGED: smaller text size */}
              <MenuItem
                onClick={handleConfigure}
                sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
              >
                Configure
              </MenuItem>

               {/* NEW – explicit Duplicate for templates */}
              {onDuplicateTemplate && (
                <MenuItem
                  sx={{ fontSize:'0.75rem', lineHeight:1.2 }}
                  onClick={() => {
                    handleMenuClose();
                    onDuplicateTemplate(form);
                  }}
                >
                  Duplicate
                </MenuItem>
              )}

              {canUnpublish && (
                <MenuItem
                  onClick={handleRemoveFromGallery}
                  sx={{ fontSize:'0.75rem', lineHeight:1.2 }}
                >
                  Remove from gallery
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </CardContent>
    </Card>
  );
}
